import React, { useEffect, useMemo, useState } from 'react';
import {
  Alert, Button, Dialog, DialogActions, DialogContent, DialogTitle, Stack, TextField, Typography,
} from '@mui/material';
import type { TimetableRow } from '../../../services/timetableService';
import { fromMinutes, toMinutes } from '../timetableUtils';

export interface QuickFill {
  start: string;
  periodMinutes: number;
  periods: number;
  breakAfter: number;
  breakMinutes: number;
}

export const DEFAULT_QUICK_FILL: QuickFill = { start: '08:00', periodMinutes: 40, periods: 8, breakAfter: 4, breakMinutes: 20 };

/** A bell schedule of equal periods with one break. */
export const rowsFrom = (q: QuickFill): TimetableRow[] => {
  const rows: TimetableRow[] = [];
  let t = toMinutes(q.start);
  for (let i = 1; i <= q.periods; i += 1) {
    rows.push({ kind: 'period', label: `Period ${i}`, start: fromMinutes(t), end: fromMinutes(t + q.periodMinutes) });
    t += q.periodMinutes;
    if (i === q.breakAfter && i < q.periods && q.breakMinutes > 0) {
      rows.push({ kind: 'break', label: 'Break', start: fromMinutes(t), end: fromMinutes(t + q.breakMinutes) });
      t += q.breakMinutes;
    }
  }
  return rows;
};

interface QuickFillDialogProps {
  open: boolean;
  /** Warn that the current schedule will be replaced. */
  replacing: boolean;
  onClose: () => void;
  onApply: (rows: TimetableRow[]) => void;
}

const QuickFillDialog: React.FC<QuickFillDialogProps> = ({ open, replacing, onClose, onApply }) => {
  const [q, setQ] = useState<QuickFill>(DEFAULT_QUICK_FILL);
  useEffect(() => { if (open) setQ(DEFAULT_QUICK_FILL); }, [open]);

  const valid = /^\d\d:\d\d$/.test(q.start) && q.periods >= 1 && q.periods <= 20 && q.periodMinutes >= 15 && q.periodMinutes <= 120
    && q.breakMinutes >= 0 && q.breakMinutes <= 120;
  const rows = useMemo(() => (valid ? rowsFrom(q) : []), [q, valid]);
  const num = (key: keyof QuickFill) => (e: React.ChangeEvent<HTMLInputElement>) =>
    setQ((prev) => ({ ...prev, [key]: Math.max(0, parseInt(e.target.value, 10) || 0) }));

  return (
    <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth>
      <DialogTitle>Quick fill</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ pt: 1 }}>
          <Typography variant="body2" color="text.secondary">Equal periods with one break. You can fine-tune any row afterwards.</Typography>
          <Stack direction="row" spacing={1.5}>
            <TextField label="School starts" type="time" size="small" fullWidth value={q.start} onChange={(e) => setQ((p) => ({ ...p, start: e.target.value }))} InputLabelProps={{ shrink: true }} />
            <TextField label="Periods" type="number" size="small" fullWidth value={q.periods} onChange={num('periods')} inputProps={{ min: 1, max: 20 }} />
          </Stack>
          <TextField label="Period length (minutes)" type="number" size="small" value={q.periodMinutes} onChange={num('periodMinutes')} inputProps={{ min: 15, max: 120 }} />
          <Stack direction="row" spacing={1.5}>
            <TextField label="Break after period" type="number" size="small" fullWidth value={q.breakAfter} onChange={num('breakAfter')} inputProps={{ min: 0, max: q.periods }} helperText="0 for no break" />
            <TextField label="Break (minutes)" type="number" size="small" fullWidth value={q.breakMinutes} onChange={num('breakMinutes')} inputProps={{ min: 0, max: 120 }} />
          </Stack>
          {valid && rows.length > 0 && (
            <Typography variant="caption" color="text.secondary">
              {q.periods} periods, {rows[0].start} to {rows[rows.length - 1].end}
            </Typography>
          )}
          {replacing && <Alert severity="info" variant="outlined">This replaces the current bell schedule.</Alert>}
        </Stack>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={onClose} color="inherit">Cancel</Button>
        <Button variant="contained" disabled={!valid} onClick={() => onApply(rows)}>Apply</Button>
      </DialogActions>
    </Dialog>
  );
};

export default QuickFillDialog;
